import { Component, HostBinding, Input, inject, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { LabIconComponent } from '../lab-icon/lab-icon.component';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { SolIcon } from '@sol/icons';
import {
  AuthenticationService,
  NavigationService,
  PopoverService,
  SolLoader,
  SolMenuGroup,
  SolPopoverAlignment,
  SolPopoverRef,
  SolRouteId,
  SolText,
  TranslatePipe,
} from '@sol/components';
import { LabPopoverMenuContentComponent } from '../lab-popover-menu-content/lab-popover-menu-content.component';
import { showLabMenu } from '../../tools/lab-popover-menu';
import { NavigationTarget } from '@sol/components/tools/router-tools';
import { SolNavbarExpandedEvent, LabNavbarItemComponent } from '../lab-navbar-item/lab-navbar-item.component';
import { LabLoaderComponent } from '../lab-loader/lab-loader.component';
import { LabAvatarComponent } from '../lab-avatar/lab-avatar.component';
import { SolNavbarState } from './lab-navbar.state';
import { PlaygroundThemeModeService, PlaygroundThemeMode } from '../../services/playground-theme-mode.service';
import { PlaygroundRouteId } from '../../models/playground-route-id';
import { coerceLabSolText } from '../../tools/lab-sol-text-input';

@Component({
  selector: 'lab-navbar',
  standalone: true,
  imports: [LabIconComponent, LabNavbarItemComponent, TranslatePipe, LabLoaderComponent, LabAvatarComponent],
  templateUrl: './lab-navbar.component.html',
  styleUrl: './lab-navbar.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: { role: 'navigation', 'aria-label': 'Main navigation' },
})
export class LabNavbarComponent extends AbstractComponent<SolNavbarState> implements OnDestroy {
  protected readonly SolIcon = SolIcon;
  protected readonly SolLoader = SolLoader;

  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly popoverService = inject(PopoverService);
  private readonly themeModeService = inject(PlaygroundThemeModeService);
  private readonly nameTextState = signal<SolText>(SolText.empty());
  private readonly explicitNameTextState = signal<SolText | undefined>(undefined);
  private readonly authenticationNameTextState = signal<SolText>(SolText.empty());

  private readonly themeModeByRouteId: Record<string, PlaygroundThemeMode> = {
    [PlaygroundRouteId.mode_light]: 'light',
    [PlaygroundRouteId.mode_dark]: 'dark',
    [PlaygroundRouteId.mode_night]: 'night',
    [PlaygroundRouteId.mode_high_contrast]: 'high-contrast',
  };

  // Computed
  protected readonly menu$ = this.compute(state => state.menu);
  protected readonly system$ = this.compute(state => state.system);
  protected readonly name$ = this.compute(state => state.name);
  protected readonly nameText$ = this.nameTextState.asReadonly();
  protected readonly initials$ = this.compute(state => state.initials);
  protected readonly activeRouteIds$ = this.compute(state => state.activeRouteIds);
  protected readonly expandedIds$ = this.compute(state => state.expandedIds);
  protected readonly hostClasses$ = this.compute(state => state.hostClasses);
  protected readonly appDisplayName$ = this.compute(state => state.appDisplayName);
  protected readonly appDisplayIcon$ = this.compute(state => state.appDisplayIcon);
  protected readonly appsVisible$ = this.compute(state => state.appsVisible);
  protected readonly appsDropdownVisible$ = this.compute(state => state.appsDropdownVisible);
  protected readonly appHover$ = this.compute(state => state.appHover);
  protected readonly loading$ = this.compute(state => state.loading);

  // Cache
  protected popoverMenuRef?: SolPopoverRef<LabPopoverMenuContentComponent>;

  constructor() {
    super(SolNavbarState.of());

    // Listen to navbar data
    this.navigationService.watch(nss => {
      const app = nss.apps.find(candidate => candidate.id === nss.appId && candidate.hasAuthority);
      const appMenuItems = nss.appMenu.flatMap(group => group.items);
      const appDisplayItem = appMenuItems.length === 1 ? appMenuItems[0] : undefined;
      const appsVisible = appMenuItems.length > 0;
      const appsDropdownVisible = appMenuItems.length > 1;
      const appDisplayName = app?.name ?? appDisplayItem?.name ?? 'lab-navbar.apps.choose';
      const appDisplayIcon = app?.icon ?? appDisplayItem?.icon;
      this.update(state =>
        state.withNavigation(
          app,
          nss.appMenu,
          appsVisible,
          appsDropdownVisible,
          appDisplayName,
          appDisplayIcon,
          nss.navbar.menu,
          nss.navbar.system,
          nss.navbar.account,
          nss.navbarVisible,
          nss.activeRouteStatus === 'loading',
        ),
      );
    });
    this.navigationService.watchComputed(
      nss => nss.activeNavbarItems,
      activeNavbarItems => {
        const activeRouteIds = activeNavbarItems.map(item => item.id);
        this.update(state => state.withActiveNavbarItems(activeRouteIds));
      },
    );

    // Listen to name changes
    this.authenticationService.watchComputed(
      state => state.name || '',
      name => {
        // Authentication may be disabled in the playground. Keep that empty
        // auth value from wiping out an explicit account name passed by the app.
        this.authenticationNameTextState.set(SolText.ofRaw(name));
        this.syncNameText();
      },
    );
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClasses$();
  }

  // SolNavbarState still stores the account name as a string, so lab keeps the
  // SolText source locally and bridges the raw value into state.
  @Input({ transform: coerceLabSolText })
  public set name(value: SolText) {
    this.explicitNameTextState.set(value.isEmpty() ? undefined : value);
    this.syncNameText();
  }

  @Input()
  public set initials(initials: string) {
    this.update(state => state.withInitials(initials));
  }

  @Input()
  public set loading(loading: boolean) {
    this.update(state => state.withLoading(loading));
  }

  @Input()
  public set navbarVisible(navbarVisible: boolean) {
    this.update(state => state.withNavbarVisible(navbarVisible));
  }

  ngOnDestroy(): void {
    this.popoverMenuRef?.close();
  }

  protected onClick(id: SolRouteId, newTab: boolean = false) {
    this.navigationService.navigate(id, {
      target: newTab ? NavigationTarget.NewTab : undefined,
    });
  }

  protected onSystemClick(id: SolRouteId, event: MouseEvent, newTab: boolean = false) {
    event.stopPropagation();

    // Navigate if no children
    const state = this.state;
    const shouldNavigate = state.system.find(item => item.id === id)!.children.length === 0;
    if (shouldNavigate) {
      this.onClick(id, newTab);
      return;
    }

    // Otherwise show menu
    const popoverMenu = state.systemMenus.get(id)!;
    this.showMenu(popoverMenu, event, false);
  }

  protected onAccountClick(event: MouseEvent) {
    this.showMenu(this.state.accountMenu, event, true);
  }

  protected onAppClick(event: MouseEvent) {
    this.update(state => state.withAppHover(true));
    this.showMenu(this.state.appMenu, event, true);
  }

  protected onNavbarHide(event: MouseEvent) {
    event.stopPropagation();
    this.navigationService.updateNavbarVisible(false);
  }

  protected onExpanded(event: SolNavbarExpandedEvent) {
    this.update(state => {
      let expandedIds = state.expandedIds;
      const isExpanded = expandedIds.includes(event.id);
      if (isExpanded) {
        expandedIds = expandedIds.filter(id => id !== event.id);
      } else {
        expandedIds = [...expandedIds, event.id];
      }
      return state.withExpandedIds(expandedIds);
    });
  }

  private showMenu(menu: SolMenuGroup[], event: MouseEvent, parentWidth: boolean) {
    this.popoverMenuRef?.close();
    this.popoverMenuRef = showLabMenu(
      this.popoverService,
      menu,
      actionId => {
        if (actionId === 'logout') {
          this.authenticationService.logout();
        }
        const themeMode = this.themeModeByRouteId[actionId];
        if (themeMode) {
          this.themeModeService.set(themeMode);
          return true;
        }
        this.onClick(actionId as SolRouteId);
        return true;
      },
      {
        afterClose: () => {
          this.update(state => state.withAppHover(false));
        },
        align: SolPopoverAlignment.bottomLeft,
        boundsEvent: event,
        width: parentWidth ? 'parent-grow' : 'auto',
      },
    );
  }

  private setNameText(value: SolText): void {
    this.nameTextState.set(value);
    this.update(state => state.withName(value.text));
  }

  private syncNameText(): void {
    this.setNameText(this.explicitNameTextState() ?? this.authenticationNameTextState());
  }
}
